/**
 * Parser CSV robuste qui gère :
 * - Les champs avec virgules (entourés de guillemets)
 * - Les sauts de ligne dans les champs (multi-lignes)
 * - Les guillemets échappés
 */

export interface ParsedCSVRow {
  [key: string]: string;
}

/**
 * Parse une ligne CSV en tenant compte des guillemets et des virgules dans les champs
 * Les guillemets externes sont supprimés, mais le contenu est préservé
 */
function parseCSVLine(line: string): string[] {
  const fields: string[] = [];
  let currentField = '';
  let insideQuotes = false;
  let fieldStartedWithQuote = false;
  let i = 0;

  while (i < line.length) {
    const char = line[i];
    const nextChar = i + 1 < line.length ? line[i + 1] : null;

    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        // Guillemet échappé (double guillemet) - on garde un seul guillemet
        currentField += '"';
        i += 2;
      } else if (!insideQuotes && currentField === '') {
        // Début d'un champ entre guillemets
        insideQuotes = true;
        fieldStartedWithQuote = true;
        i++;
      } else if (insideQuotes) {
        // Fin des guillemets
        insideQuotes = false;
        i++;
      } else {
        // Guillemet dans un champ non-quoté (cas rare mais possible)
        currentField += char;
        i++;
      }
    } else if (char === ',' && !insideQuotes) {
      // Fin d'un champ
      fields.push(currentField);
      currentField = '';
      fieldStartedWithQuote = false;
      i++;
    } else {
      currentField += char;
      i++;
    }
  }

  // Ajouter le dernier champ
  fields.push(currentField);

  return fields;
}

/**
 * Parse un fichier CSV complet
 * Gère les lignes multi-lignes dans les champs (historique)
 * Gère aussi les retours chariot Windows (\r\n) et Unix (\n)
 */
export function parseCSV(csvContent: string): ParsedCSVRow[] {
  // Normaliser les retours chariot (Windows \r\n -> \n)
  const normalized = csvContent.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  
  const rows: ParsedCSVRow[] = [];
  let i = 0;
  let insideQuotes = false;
  let currentRow = '';
  let expectedFieldCount = 0;
  const headers: string[] = [];
  let isFirstLine = true;

  // Parser caractère par caractère pour gérer correctement les sauts de ligne dans les champs
  while (i < normalized.length) {
    const char = normalized[i];
    const nextChar = i + 1 < normalized.length ? normalized[i + 1] : null;

    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        // Guillemet échappé (double guillemet) - on garde un seul guillemet dans le champ
        currentRow += '"';
        i += 2;
        continue;
      } else {
        // Toggle insideQuotes - on ne garde pas le guillemet dans currentRow
        // car parseCSVLine va le gérer correctement
        insideQuotes = !insideQuotes;
        currentRow += char; // On garde quand même pour que parseCSVLine puisse détecter les champs quotés
        i++;
        continue;
      }
    }

    // Gérer les sauts de ligne
    if (char === '\n') {
      if (insideQuotes) {
        // Saut de ligne dans un champ (on le garde pour préserver le formatage)
        currentRow += '\n';
        i++;
        continue;
      } else {
        // Fin de ligne réelle (hors guillemets)
        // La ligne logique est complète, on peut la traiter
        if (isFirstLine) {
          // Première ligne = headers
          const headerFields = parseCSVLine(currentRow);
          if (headerFields.length > 0) {
            headers.push(...headerFields);
            expectedFieldCount = headers.length;
            isFirstLine = false;
          }
          currentRow = '';
        } else {
          // Ligne de données
          const fields = parseCSVLine(currentRow);
          
          // Vérifier que le nombre de champs correspond
          // Si ce n'est pas le cas, on essaie quand même de mapper ce qu'on peut
          if (fields.length === expectedFieldCount || fields.length > 0) {
            const row: ParsedCSVRow = {};
            headers.forEach((header, index) => {
              const cleanHeader = header.trim();
              // Ne pas trim la valeur pour préserver les espaces et sauts de ligne
              // Mais nettoyer les guillemets externes si présents
              let value = fields[index] || '';
              // Si le champ commence et se termine par des guillemets, les retirer
              if (value.startsWith('"') && value.endsWith('"') && value.length > 1) {
                value = value.slice(1, -1);
              }
              row[cleanHeader] = value;
            });
            rows.push(row);
          }
          currentRow = '';
        }
        
        i++;
        continue;
      }
    }

    // Caractère normal
    currentRow += char;
    i++;
  }

  // Traiter la dernière ligne si elle existe (pas de \n final)
  if (currentRow.trim()) {
    if (isFirstLine) {
      // Headers
      const headerFields = parseCSVLine(currentRow);
      headers.push(...headerFields);
      expectedFieldCount = headers.length;
    } else {
      // Dernière ligne de données
      const fields = parseCSVLine(currentRow);
      if (fields.length === expectedFieldCount || fields.length > 0) {
        const row: ParsedCSVRow = {};
        headers.forEach((header, index) => {
          const cleanHeader = header.trim();
          let value = fields[index] || '';
          // Si le champ commence et se termine par des guillemets, les retirer
          if (value.startsWith('"') && value.endsWith('"') && value.length > 1) {
            value = value.slice(1, -1);
          }
          row[cleanHeader] = value;
        });
        rows.push(row);
      }
    }
  }

  return rows;
}

/**
 * Interface pour une ligne de lead importée
 */
export interface ImportLeadRow {
  nomDuLead: string;
  email: string;
  telephone: string;
  ville: string;
  source: string;
  statut: string;
  prochainRappel: string;
  historique: string;
  piecesJointes: string;
  dateCreation: string;
  dateAjoutLead: string;
}

/**
 * Convertit une ligne CSV parsée en structure ImportLeadRow
 */
export function mapCSVRowToLead(row: ParsedCSVRow): ImportLeadRow {
  return {
    nomDuLead: row['Nom du lead'] || row['nom du lead'] || '',
    email: row['Email'] || row['email'] || '',
    telephone: row['Téléphone'] || row['telephone'] || '',
    ville: row['Ville'] || row['ville'] || '',
    source: row['Source'] || row['source'] || '',
    statut: row['Statut'] || row['statut'] || '',
    prochainRappel: row['Prochain rappel'] || row['prochain rappel'] || '',
    historique: row['Historique'] || row['historique'] || '',
    piecesJointes: row['Pièces jointes'] || row['pieces jointes'] || '',
    dateCreation: row['Date Création'] || row['date création'] || '',
    dateAjoutLead: row['Date Ajout Lead'] || row['date ajout lead'] || ''
  };
}
